'use client';

import { useStore } from '@/providers/app-store-provider';
import appCommunicationManager from '@/utils/app-communication-manager';
import logger from '@/utils/client-logger';
import { EAppCommunicationFuncName } from '@/utils/enums/app-sdk';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useCallback, useEffect, useMemo, useState } from 'react';
import ActivityBanner from '../components/ActivityBanner';
import FaqAccordion from '../spring-sale/components/FaqAccordion';
import TrendProductList from './components/TrendProductList';
import YouMightLike from './components/YouMightLike';
import { useTrendEyeglassesTracking } from './hooks/useTrendEyeglassesTracking';
import { TrendEyeglassesClientProps, TrendEyeglassesPageData } from './types';

export default function TrendEyeglassesClientPage({
  serverData,
  deviceType,
  bannerData,
  recommendList,
  faqData,
}: TrendEyeglassesClientProps) {
  const [pageData] = useState<TrendEyeglassesPageData>(serverData);
  const [fristRecommendListLoaded, setFristRecommendListLoaded] = useState(false); // 推荐商品区域加载完成
  const [allRecommendListLoaded, setAllRecommendListLoaded] = useState(false); // YouMightLike加载完成
  const [inAppH5, setInAppH5] = useState([EPlatform.IOS, EPlatform.ANDROID].includes(deviceType));
  const [isAppCommunicationReady, setIsAppCommunicationReady] = useState(!inAppH5);

  const setToken = useStore((state) => state.setToken);
  const setUserInfo = useStore((state) => state.setUserInfo);
  const refreshAccountInfo = useStore((state) => state.refreshAccountInfo);
  const setIsShowFooterNav = useStore((state) => state.setIsShowFooterNav);
  const isWap = deviceType !== EPlatform.WEB;

  // 缓存Banner数据和aspectRatio
  const actualBannerData = useMemo(
    () =>
      isWap
        ? bannerData || pageData?.wap_banner || pageData?.banner
        : bannerData || pageData?.banner,
    [bannerData, pageData?.banner, pageData?.wap_banner, isWap],
  );

  const bannerAspectRatio = useMemo(
    () => (typeof actualBannerData === 'object' ? actualBannerData.aspectRatio : undefined),
    [actualBannerData],
  );

  // 初始化埋点追踪
  const { trackPageView, trackPageLeave } = useTrendEyeglassesTracking();

  // 检测是否在APP内
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const { userAgent } = navigator;
      const isInApp = userAgent.includes('nextmar-xms-app');
      setInAppH5(isInApp);
      if (isInApp) {
        setIsAppCommunicationReady(false);
      }
    }
  }, []);

  // App通信初始化
  useEffect(() => {
    if (inAppH5) {
      const handleAppCommunication = async () => {
        try {
          appCommunicationManager.registerCallback(
            EAppCommunicationFuncName.APP_LOGIN_SUCCESS,
            async (_token) => {
              logger.log('Trend Eyeglasses App登录token:', _token);
              if (_token !== 'ClearToken') {
                setToken(_token);
                await refreshAccountInfo();
                setIsAppCommunicationReady(true);
              } else {
                // 清空当前用户信息
                setToken('');
                setUserInfo({});
                setIsAppCommunicationReady(true);
              }
            },
            {},
            'BackTokenInfo' as any,
          );
        } catch (e) {
          logger.error('App通信初始化失败:', e);
        }
      };

      handleAppCommunication();

      const timeout = setTimeout(() => {
        if (!isAppCommunicationReady) {
          logger.warn('App通信超时，将以游客状态继续渲染页面');
          setIsAppCommunicationReady(true);
        }
      }, 5000);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [inAppH5]);

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      if (inAppH5) {
        appCommunicationManager.cleanup();
      }
    };
  }, [inAppH5]);

  // 页面加载完成后触发页面浏览埋点
  useEffect(() => {
    if (pageData) {
      if (isWap) {
        setTimeout(() => {
          setIsShowFooterNav(false);
        }, 50);
      }
      trackPageView();
    }
    return () => {
      trackPageLeave();
    };
  }, [isWap]);

  // 第一个推荐商品区域加载完成回调
  const handleFirstRecommendLoadComplete = useCallback((isComplete: boolean) => {
    setFristRecommendListLoaded(isComplete);
  }, []);

  // YouMightLike推荐商品加载完成回调
  const handleRecommendLoadComplete = useCallback(
    (isComplete: boolean) => {
      setAllRecommendListLoaded(isComplete);
      if (isComplete && isWap) {
        setIsShowFooterNav(true);
      }
    },
    [isWap, setIsShowFooterNav],
  );

  if (!pageData) {
    return null;
  }

  return (
    <div className="bg-white">
      {/* Banner区域 */}
      {actualBannerData && typeof actualBannerData === 'object' && (
        <ActivityBanner
          data={{
            alt: actualBannerData.image_alt,
            image: actualBannerData.image,
            type: actualBannerData.type,
          }}
          aspectRatio={bannerAspectRatio}
        />
      )}

      {/* 推荐商品区域 - App内嵌时需要等待通信完成 */}
      {recommendList && isAppCommunicationReady && (
        <TrendProductList
          deviceType={deviceType}
          recommendList={recommendList}
          // isInApp={inAppH5}
          onLoadComplete={handleFirstRecommendLoadComplete}
        />
      )}

      {/* You Might Like 推荐商品 - 等待第一个推荐商品区域加载完成后显示 */}
      {fristRecommendListLoaded && (
        <YouMightLike
          deviceType={deviceType}
          onLoadComplete={handleRecommendLoadComplete}
          isInApp={inAppH5}
          receivedAppWishData={[]}
        />
      )}

      {/* FAQ区域 - 只在推荐商品加载完成后显示 */}
      {allRecommendListLoaded && faqData && (
        <div className="faq-section">
          <FaqAccordion
            backgroundColor="bg-[#f4f4f4]"
            faqData={{ ...faqData, title: faqData.title }}
          />
        </div>
      )}
    </div>
  );
}
